import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Alert,
  Snackbar,
  Paper,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AdminLayout from './components/AdminLayout';
import ProblemPoolList from './components/problem-pool/ProblemPoolList';
import ProblemPoolFilters from './components/problem-pool/ProblemPoolFilters';
import ProblemPoolFormDialog from './components/problem-pool/ProblemPoolFormDialog';
import CategoryManager from './components/problem-pool/CategoryManager';
import TagManager from './components/problem-pool/TagManager';
import {
  getPoolProblems,
  deletePoolProblem,
  getCategories,
  getTags,
  PoolProblem,
  ProblemCategory,
  ProblemTag,
  PoolProblemFilters
} from '../../services/problemPoolService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`problem-pool-tabpanel-${index}`}
      aria-labelledby={`problem-pool-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ProblemPoolPage: React.FC = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Pool problems state
  const [problems, setProblems] = useState<PoolProblem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit] = useState(20);

  // Filters state
  const [filters, setFilters] = useState<PoolProblemFilters>({
    sortBy: 'created_at',
    sortOrder: 'desc',
    page: 1,
    limit: 20
  });

  // Categories and tags for filters
  const [categories, setCategories] = useState<ProblemCategory[]>([]);
  const [tags, setTags] = useState<ProblemTag[]>([]);

  // Dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState<PoolProblem | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch categories and tags
  const fetchCategoriesAndTags = useCallback(async () => {
    try {
      const [categoriesData, tagsData] = await Promise.all([
        getCategories(true),
        getTags(true)
      ]);
      setCategories(categoriesData);
      setTags(tagsData);
    } catch (err) {
      console.error('Error fetching categories/tags:', err);
    }
  }, []);

  // Fetch problems
  const fetchProblems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPoolProblems({
        ...filters,
        page,
        limit
      });
      setProblems(result.problems);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setError(error.response?.data?.message || error.message || 'Failed to load problems');
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit]);

  // Initial data load
  useEffect(() => {
    fetchCategoriesAndTags();
  }, [fetchCategoriesAndTags]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchProblems();
    }
  }, [fetchProblems, tabValue]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFiltersChange = (newFilters: PoolProblemFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleOpenCreateDialog = () => {
    setSelectedProblem(null);
    setIsEditMode(false);
    setFormDialogOpen(true);
  };

  const handleOpenEditDialog = (problem: PoolProblem) => {
    setSelectedProblem(problem);
    setIsEditMode(true);
    setFormDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setFormDialogOpen(false);
    setSelectedProblem(null);
    setIsEditMode(false);
  };

  const handleFormSuccess = () => {
    handleCloseDialog();
    fetchProblems();
    fetchCategoriesAndTags(); // Refresh counts
    setSnackbar({
      open: true,
      message: isEditMode ? 'Problem updated successfully' : 'Problem created successfully',
      severity: 'success'
    });
  };

  const handleDeleteProblem = async (problemId: number) => {
    try {
      await deletePoolProblem(problemId);
      fetchProblems();
      fetchCategoriesAndTags(); // Refresh counts
      setSnackbar({
        open: true,
        message: 'Problem deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete problem',
        severity: 'error'
      });
    }
  };

  const handleCategoriesChange = () => {
    fetchCategoriesAndTags();
  };

  const handleTagsChange = () => {
    fetchCategoriesAndTags();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Problem Pool
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage reusable math problems that can be added to any session
            </Typography>
          </Box>
          {tabValue === 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              New Problem
            </Button>
          )}
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Problems (${total})`} />
            <Tab label={`Categories (${categories.length})`} />
            <Tab label={`Tags (${tags.length})`} />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Filters Sidebar */}
            <Grid item xs={12} md={3}>
              <ProblemPoolFilters
                categories={categories}
                tags={tags}
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </Grid>

            {/* Problems List */}
            <Grid item xs={12} md={9}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ProblemPoolList
                  problems={problems}
                  total={total}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onEdit={handleOpenEditDialog}
                  onDelete={handleDeleteProblem}
                />
              )}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <CategoryManager
            categories={categories}
            onCategoriesChange={handleCategoriesChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TagManager
            tags={tags}
            onTagsChange={handleTagsChange}
          />
        </TabPanel>

        {/* Form Dialog */}
        <ProblemPoolFormDialog
          key={selectedProblem?.id ?? 'new'}
          open={formDialogOpen}
          onClose={handleCloseDialog}
          problem={selectedProblem}
          isEditMode={isEditMode}
          categories={categories}
          tags={tags}
          onSuccess={handleFormSuccess}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default ProblemPoolPage;
